"use client"

import { useRef, useState } from "react"
import Image from "next/image"
import { Mic, PhoneOff, Video } from "lucide-react"
import { CallTimer } from "@/components/call-timer"
import { useTranslations } from "next-intl"
import { HERO_BG_DEFAULT } from "@/lib/brand"
import { useDeferredHeroMedia } from "@/hooks/use-deferred-hero-media"

export default function HeroVideoMock() {
  const t = useTranslations("landing")
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const { containerRef, isActive } = useDeferredHeroMedia()
  const [videoLoaded, setVideoLoaded] = useState(false)

  return (
    <div className="relative" ref={containerRef} style={{ contain: 'layout style paint' }}>
      <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-amber-400/20 to-emerald-400/20 blur-2xl" style={{ willChange: 'auto' }} />
      <div className={`relative overflow-hidden rounded-3xl border-4 border-white/20 shadow-2xl ${HERO_BG_DEFAULT}`}>
        {/* Contenedor con aspect-ratio fijo para prevenir layout shift */}
        <div className={`relative aspect-square w-full ${HERO_BG_DEFAULT}`} style={{ minHeight: '1px' }}>
          {/* Santa main image - siempre visible como fallback */}
          <Image
            src="/videos/santa-main-poster.webp"
            alt={t("hero.santaName")}
            fill
            sizes="(min-width: 1024px) 480px, 100vw"
            className={`object-cover transition-opacity duration-300 ${
              isActive && videoLoaded ? 'opacity-0' : 'opacity-100'
            }`}
            preload
            style={{ willChange: 'opacity' }}
          />
          
          {/* Video - se muestra cuando está cargado */}
          {isActive && (
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              poster="/videos/santa-main-poster.webp"
              preload="none"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
                videoLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ willChange: 'opacity' }}
              onLoadedData={() => {
                setVideoLoaded(true)
              }}
              onCanPlay={() => {
                setVideoLoaded(true)
              }}
            >
              <source src="/videos/santa-main.mp4" type="video/mp4" />
            </video>
          )}

          {/* Call status indicator */}
          <div className="absolute top-3 left-3 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 backdrop-blur-sm">
            <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-emerald-400" />
            <span className="text-sm font-medium text-white">{t("hero.callStatus")}</span>
            <CallTimer initialSeconds={165} className="text-sm text-white/80" />
          </div>

          {/* Santa name tag */}
          <div className="absolute bottom-4 left-3 rounded-lg bg-black/60 px-3 py-2 backdrop-blur-sm">
            <p className="text-sm font-bold text-white">{t("hero.santaName")}</p>
            <p className="text-xs text-white/70">{t("hero.santaLocation")}</p>
          </div>

          {/* Child PiP video */}
          <div className="absolute right-3 top-3 h-24 w-[72px] overflow-hidden rounded-2xl border-2 border-amber-400/80 bg-black shadow-xl md:h-32 md:w-24">
            {/* Imagen siempre visible como fallback */}
            <Image
              src="/videos/child-pip-poster.webp"
              alt={t("hero.youLabel")}
              fill
              sizes="96px"
              className={`object-cover scale-x-[-1] transition-opacity duration-300 ${
                isActive && videoLoaded ? 'opacity-0' : 'opacity-100'
              }`}
              loading="lazy"
            />
            {/* Video cuando está cargado */}
            {isActive && (
              <video
                autoPlay
                loop
                muted
                playsInline
                poster="/videos/child-pip-poster.webp"
                preload="none"
                className={`absolute inset-0 h-full w-full scale-x-[-1] object-cover transition-opacity duration-300 ${
                  videoLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                onLoadedData={() => {
                  // El video principal ya maneja el estado, pero esto ayuda a sincronizar
                  if (!videoLoaded) {
                    setVideoLoaded(true)
                  }
                }}
                onCanPlay={() => {
                  if (!videoLoaded) {
                    setVideoLoaded(true)
                  }
                }}
              >
                <source src="/videos/child-pip.mp4" type="video/mp4" />
              </video>
            )}
            <div className="absolute bottom-1 left-1/2 -translate-x-1/2 rounded bg-black/70 px-2 py-0.5 text-[10px] text-white backdrop-blur-sm z-10">
              {t("hero.youLabel")}
            </div>
          </div>

          {/* Call controls */}
          <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-sm shadow-lg md:h-12 md:w-12">
              <Mic className="h-5 w-5 text-white" />
            </div>

            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500 shadow-lg md:h-14 md:w-14">
              <PhoneOff className="h-5 w-5 text-white md:h-6 md:w-6" />
            </div>

            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 bg-white/20 backdrop-blur-sm shadow-lg md:h-12 md:w-12">
              <Video className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

